import type { SearchUser } from '~/features/common/hooks';
import { Stack, AutocompleteField, Row, Col, Chip, ListBox, Avatar } from '@workramp/ui';
import { I18n } from '~/common/explicit-exports';
import { getLettersForAvatar } from '~/utils';
import { MixPanel } from '~/common';
import { useGetInternalUsersByIdQuery } from '~/features/common/hooks';
import { LearnerSearchMixpanelEvents } from '../../constants';
import { useSearch } from '../../providers';

interface UpdatedByAutocompleteProps {
  autoFocus?: boolean;
  selectedUpdatedByUserIds: string[];
  setSelectedUpdatedByUserIds: (query: string[]) => void;
}

export const UpdatedByAutocomplete = ({
  autoFocus,
  selectedUpdatedByUserIds,
  setSelectedUpdatedByUserIds,
}: UpdatedByAutocompleteProps) => {
  const UPDATED_BY = I18n.t('js.learner.search.advanced_search.updated_by');
  const SEARCH_BY_NAME_OR_EMAIL = I18n.t('js.learner.search.advanced_search.search_by_name_or_email');
  const ERROR_SEARCHING_USERS = I18n.t('js.learner.search.advanced_search.error_searching_users');

  const { updatedBySearchTerm, setUpdatedBySearchTerm, updatedByFilterUsers, updatedByFilterUsersHasError } =
    useSearch();

  const selectedUpdatedByUsersQuery = useGetInternalUsersByIdQuery({
    userIds: selectedUpdatedByUserIds,
    enabled: selectedUpdatedByUserIds.length > 0,
  });
  const selectedUpdatedByUsers = selectedUpdatedByUsersQuery.data || [];

  const handleOnSelectionChange = (id: string) => {
    if (id) {
      setSelectedUpdatedByUserIds([...selectedUpdatedByUserIds, id]);
    }
    setUpdatedBySearchTerm('');
    MixPanel.track(LearnerSearchMixpanelEvents.LEARNER_ADVANCED_SEARCH_SELECTS_UPDATED_BY_FILTER_VALUE);
  };

  const onDelete = (user: SearchUser) => {
    setSelectedUpdatedByUserIds(selectedUpdatedByUserIds.filter((u) => u !== String(user.userId)));
  };

  const hasError = updatedByFilterUsersHasError || selectedUpdatedByUsersQuery.error;

  return (
    <Stack size={2}>
      <AutocompleteField
        data-testid="updated-by-autocomplete"
        autoFocus={autoFocus ?? false}
        label={UPDATED_BY}
        placeholder={SEARCH_BY_NAME_OR_EMAIL}
        items={updatedByFilterUsers || []}
        onSelectionChange={handleOnSelectionChange}
        onBlur={() => setUpdatedBySearchTerm('')}
        onInputChange={setUpdatedBySearchTerm}
        inputValue={updatedBySearchTerm}
        hasError={Boolean(hasError)}
        errorMessage={hasError ? ERROR_SEARCHING_USERS : undefined}
      >
        {(item) => (
          <ListBox.Item
            key={item.userId}
            avatar={<Avatar letters={getLettersForAvatar(item?.name)} imgUrl={item?.avatar} />}
            description={item.value}
          >
            {item.name}
          </ListBox.Item>
        )}
      </AutocompleteField>
      {selectedUpdatedByUsers.length > 0 && (
        <Row gapY={2} gapX={2}>
          {selectedUpdatedByUsers.map((author) => (
            <Col key={author.userId} xs="auto">
              <Chip onDelete={() => onDelete(author)}>{author.label}</Chip>
            </Col>
          ))}
        </Row>
      )}
    </Stack>
  );
};
